import { toE164, tt } from '@agos/shared'

/**
 * The SMS contact book — FRONTEND.md §9.241, FB-401.
 *
 * Four endpoints served since the port and called by nothing, against **20 rows of the owner's own
 * data**. Legacy has the screen at the foot of its SMS tab (`loyalty-program.tsx:4415`) and AGOS
 * never ported it.
 *
 * **These are the recipients of an SMS campaign**, not an address book: legacy's composer offers
 * *"SMS (SMS kontakty)"* and says the message goes to all of them. Nothing here sends — that is a
 * different control with a `BLOCKED.md` guard on it — but a manager who cannot read the list cannot
 * know who a campaign would reach.
 */

export interface SmsContactRow {
  id: number
  name: string | null
  phone: string
  createdAt: string | null
}

/** Fifty a page, which is legacy's `PAGE`. */
export const CONTACTS_PAGE = 50

export function contactsOf(payload: unknown): SmsContactRow[] {
  const rows = (payload as { contacts?: unknown } | undefined)?.contacts
  if (!Array.isArray(rows)) return []
  return rows.flatMap((row) => {
    const entry = row as { id?: unknown; name?: unknown; phone?: unknown; createdAt?: unknown }
    if (typeof entry.id !== 'number') return []
    return [
      {
        id: entry.id,
        name: typeof entry.name === 'string' && entry.name.trim() !== '' ? entry.name : null,
        phone: typeof entry.phone === 'string' ? entry.phone : '',
        createdAt: typeof entry.createdAt === 'string' ? entry.createdAt : null,
      },
    ]
  })
}

/**
 * How many there are in total, which is **not** how many came back.
 *
 * The endpoint pages, so a screen that counted `contacts.length` would say *50* forever. Legacy
 * prints the server's `total` beside the page for the same reason.
 */
export function totalOf(payload: unknown): number {
  const total = (payload as { total?: unknown } | undefined)?.total
  return typeof total === 'number' && total >= 0 ? total : contactsOf(payload).length
}

export function pageCount(total: number): number {
  return total <= 0 ? 1 : Math.ceil(total / CONTACTS_PAGE)
}

/** `51 – 100 z 214` — which rows are on screen, since only fifty of them are. */
export function pagerNote(total: number, page: number): string {
  if (total <= 0) return tt('Žiadne kontakty')
  const first = page * CONTACTS_PAGE + 1
  const last = Math.min(total, (page + 1) * CONTACTS_PAGE)
  return `${first} – ${last} ${tt('z')} ${total}`
}

export type ContactProblem = 'phone-missing' | 'phone-unusable'

/**
 * What is wrong with the form, before it is sent.
 *
 * **The phone is the record and the name is decoration** — the column is `NOT NULL` and a campaign
 * sends to the number. `toE164` is the same helper the rest of the app stores numbers with, so a
 * contact typed here matches one that arrived through an order; it answers `null` when there is not
 * enough to work with, which is the second problem rather than a silently invented number.
 */
export function contactProblems(phone: string): ContactProblem[] {
  if (phone.trim() === '') return ['phone-missing']
  return toE164(phone) === null ? ['phone-unusable'] : []
}

export function contactProblemNote(problem: ContactProblem): string {
  if (problem === 'phone-missing') return tt('Telefónne číslo je povinné.')
  return tt('Toto číslo sa nedá použiť - skontrolujte ho.')
}

export function maySaveContact(phone: string): boolean {
  return contactProblems(phone).length === 0
}

/**
 * The payload, with the number normalised the way every other screen stores it.
 *
 * An empty name is omitted rather than sent as `""`: the column is nullable and *"no name"* and
 * *"a name that is the empty string"* should not become two states in the data.
 */
export function contactPayload(name: string, phone: string): { name?: string; phone: string } {
  const trimmed = name.trim()
  const stored = toE164(phone) ?? phone.trim()
  return trimmed === '' ? { phone: stored } : { name: trimmed, phone: stored }
}
